import type { AdminOrgDto, AdminOrgMemberDto } from '@carcheck/shared';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { MembersTable } from './MembersTable';
import { SectionCard } from './SectionCard';
import { removeOrgMember, useAdminOrgMembers } from '../../../api/admin-orgs';
import { ConfirmationDialog } from '../../../components/feedback';
import { Seats } from '../_lib/companyColumns';
import { useOrgWrite } from '../_lib/useOrgWrite';

/**
 * «Сотрудники» (AUT-403), after `companies-detail.html?tab=members`.
 *
 * Active members by default; «С ушедшими» adds the removed ones
 * (`status=all`). Names / phones arrive masked without `view_pii`.
 * «Удалить» (behind a confirm that says the member code rotates — the API
 * rotates it in the same transaction, decision 2) is on active rows of a
 * non-terminated company; the owner row's «Сменить» jumps to «Коды»,
 * where a new owner code hands the company over. The mockup's «Отчётов»
 * column and «по коду владельца / компании» line need data the API does not
 * send yet (CP-05), so they are not rendered.
 */
export const MembersTab = ({
  org,
  onChangeOwner,
}: {
  org: AdminOrgDto;
  onChangeOwner: () => void;
}): ReactElement => {
  const { t } = useTranslation();
  const [withRemoved, setWithRemoved] = useState(false);
  const [removing, setRemoving] = useState<AdminOrgMemberDto | null>(null);
  const query = useAdminOrgMembers(org.id, withRemoved ? 'all' : 'active');
  const write = useOrgWrite();
  const ns = 'companies.members';

  const confirmRemove = (): void => {
    if (!removing) return;
    void write
      .run(() => removeOrgMember(org.id, removing.userId), `${ns}.removeDialog.success`)
      .then((ok) => {
        if (ok) setRemoving(null);
      });
  };

  const toggle = (
    <Button
      size="small"
      variant={withRemoved ? 'contained' : 'outlined'}
      onClick={() => setWithRemoved((v) => !v)}
    >
      {t(`${ns}.withRemoved`)}
    </Button>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <SectionCard title={t(`${ns}.title`)} action={toggle}>
        <Seats active={org.activeMembers} max={org.maxMembers} />
        {query.isLoading ? <CircularProgress size={24} /> : null}
        {query.error && !query.data ? <Alert severity="error">{t(`${ns}.loadError`)}</Alert> : null}
        {query.data ? (
          <MembersTable
            members={query.data.items}
            actionsEnabled={org.status !== 'terminated'}
            onRemove={setRemoving}
            onChangeOwner={onChangeOwner}
          />
        ) : null}
      </SectionCard>
      <Typography variant="caption" color="text.secondary">
        {t(`${ns}.note`)}
      </Typography>
      <ConfirmationDialog
        open={removing !== null}
        variant="destructive"
        title={t(`${ns}.removeDialog.title`)}
        description={t(`${ns}.removeDialog.body`, {
          name: removing?.name ?? t('companies.noValue'),
        })}
        confirmLabel={t(`${ns}.removeDialog.confirm`)}
        loading={write.busy}
        onConfirm={confirmRemove}
        onCancel={() => setRemoving(null)}
      />
    </Box>
  );
};
